import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  DwComponent,
  DwComponentType,
  DwContainerOptions,
  DwExpressionService,
  DwMetaDataFormApi,
  DwMetaDataFormSectionStateService,
  DwMetaDataFormStateService,
  DwMetaDataService,
  DwMetaDataServiceToken,
  DwOrmDataService,
  DwOrmDataServiceToken,
  DwSectionReadonlyBaseComponent,
  DwUIMetaDataConfig,
  DwUIMetaDataConfigToken,
  DwUiConfigRegistryService
} from '@devwareapps/devware-cap';
import { Subject } from 'rxjs';
import { debounceTime, finalize, takeUntil } from 'rxjs/operators';

import { AppMetaData, AppMetaDataItemNames, FlightSchoolEntity } from '../../../../meta-data/app-meta-data.service';

@DwComponent({
  key: 'flight-school-marketplace-form-section',
  name: 'Flight School Marketplace - Form Section',
  componentType: DwComponentType.formSection,
  parentItemName: AppMetaDataItemNames.FlightSchool,
  isGlobal: false
})
@Component({
  selector: 'app-flight-school-marketplace-form-section',
  templateUrl: './flight-school-marketplace-form-section.component.html',
  styleUrls: ['./flight-school-marketplace-form-section.component.scss']
})
export class FlightSchoolMarketplaceFormSectionComponent extends DwSectionReadonlyBaseComponent implements OnInit, OnDestroy {
  readonly containerOptions: DwContainerOptions = {
    useContainerStyling: true,
    showTitle: true,
    containerHeaderIcon: 'fa fa-shopping-cart',
    containerClass: 'dw-form-section-container-enhanced',
    containerTitleClass: 'dw-form-section-container-enhanced-header',
    containerInnerClass: 'dw-form-section-container-enhanced-inner-padding'
  };

  marketplaceEnabled = false;
  studentProfitSharingReductionEnabled = false;
  sectionReadOnly = false;
  saving = false;

  private readonly formApi: DwMetaDataFormApi;
  private readonly destroy$ = new Subject<void>();

  constructor(
    dwExpressionService: DwExpressionService,
    dwUiConfigRegistryService: DwUiConfigRegistryService,
    @Inject(DwUIMetaDataConfigToken) uiMetaDataConfig: DwUIMetaDataConfig,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    @Inject(DwOrmDataServiceToken) private dwOrmDataService: DwOrmDataService,
    dwFormSectionStateService: DwMetaDataFormSectionStateService,
    dwFormStateService: DwMetaDataFormStateService
  ) {
    super(
      dwExpressionService,
      dwFormSectionStateService,
      dwFormStateService,
      dwUiConfigRegistryService,
      uiMetaDataConfig,
      dwMetaDataService
    );
    this.formApi = dwFormStateService.state.formApi;
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.sectionReadOnly = !!this.isSectionReadOnly();

    this.runAfterFormDataLoaded(() => this.syncFromForm());
    this.formApi.getFormDataChanges<FlightSchoolEntity>()
      .pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(() => this.syncFromForm());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  sectionReadOnlyChanged(readOnly: boolean): void {
    this.sectionReadOnly = !!readOnly;
  }

  marketplaceEnabledChanged(enabled: boolean): void {
    this.saveSetting('IsMarketplaceEnabled', enabled, this.marketplaceEnabled);
  }

  studentProfitSharingReductionEnabledChanged(enabled: boolean): void {
    this.saveSetting(
      'StudentProfitSharingReductionEnabled',
      enabled,
      this.studentProfitSharingReductionEnabled
    );
  }

  private saveSetting(
    field: 'IsMarketplaceEnabled' | 'StudentProfitSharingReductionEnabled',
    enabled: boolean,
    currentValue: boolean
  ): void {
    if (this.sectionReadOnly || this.saving) {
      return;
    }

    const currentFlightSchool = this.formApi.getFormData<FlightSchoolEntity>();
    const previousValue = !!currentFlightSchool?.[field];

    if (!currentFlightSchool?.FlightSchoolId) {
      this.setLocalSetting(field, previousValue);
      return;
    }

    if (enabled === previousValue) {
      return;
    }

    const flightSchool = AppMetaData.FlightSchool.CreateEntity();
    flightSchool.FlightSchoolId = currentFlightSchool.FlightSchoolId;
    flightSchool[field] = enabled;

    this.saving = true;
    this.dwOrmDataService.saveEntity(flightSchool)
      .pipe(
        finalize(() => this.saving = false),
        takeUntil(this.destroy$)
      )
      .subscribe(savedFlightSchool => {
        const savedValue = savedFlightSchool?.[field] ?? enabled;
        const patchData: Partial<FlightSchoolEntity> = {};
        patchData[field] = savedValue;

        this.setLocalSetting(field, savedValue);
        this.formApi.patchFormData(patchData);
        this.formApi.patchContextData('data', patchData);

      }, () => {
        this.setLocalSetting(field, previousValue);
      });
  }

  private syncFromForm(): void {
    const flightSchool = this.formApi.getFormData<FlightSchoolEntity>();
    this.marketplaceEnabled = !!flightSchool?.IsMarketplaceEnabled;
    this.studentProfitSharingReductionEnabled = !!flightSchool?.StudentProfitSharingReductionEnabled;
  }

  private setLocalSetting(
    field: 'IsMarketplaceEnabled' | 'StudentProfitSharingReductionEnabled',
    value: boolean
  ): void {
    if (field === 'IsMarketplaceEnabled') {
      this.marketplaceEnabled = value;
      return;
    }

    this.studentProfitSharingReductionEnabled = value;
  }
}
